;; Copyright (C) 2015, Doctor Evidence. All rights reserved.

(ns dre.coll
  "Collection utilities."
  {:authors ["Joël Kuiper" "Jozef Wagner"
             "Michiel Borkent"]}
  (:require
   [clojure.set :as set]
   #?(:clj [net.cgrand.macrovich :as macros])
   [clojure.walk :as walk :refer [postwalk]]
   [camel-snake-kebab.core :refer [->snake_case_keyword ->snake_case
                                   ->kebab-case-keyword ->kebab-case]]
   [dre.util :as util]
   #?(:clj [clojure.core.reducers])
   #?(:clj [clojure.tools.logging :refer [info]])
   #?(:cljs [goog.object :as gobj]))
  #?(:cljs (:require-macros [net.cgrand.macrovich :as macros]
                            ;; cljs must self refer macros
                            [dre.coll :refer [->map lazy-get]])))

;;;; Implementation details

;; TODO: This dummy info fn is used in cljs because requiring [taoensso.timbre :refer-macros [info]] throws error in storybook
#?(:cljs
   (def info (constantly nil)))

(defn lazy-mapcat*
  "Returns a lazy seq of (f x) of items x taken from collection chunk.
  coll is a collection of subsequent chunks."
  [f coll chunk]
  (lazy-seq
   (if chunk
     (cons (first chunk) (lazy-mapcat* f coll (next chunk)))
     (when-let [coll (seq coll)]
       (lazy-mapcat* f (rest coll) (seq (f (first coll))))))))


;;;; Public API

(defn provide-coll
  "Wraps x in a vector if it is not already a persistent collection,
  otherwise returns x. Returns an empty vector if nil is given."
  [x]
  (cond (coll? x) x
        (nil? x) []
        :else [x]))

(defn treduce
  "Like c.c/reduce, but converts init into transient, and result
  back into persistent object."
  [rf init coll]
  (persistent! (reduce rf (transient init) coll)))

(defn index-of
  "Returns index of first element in coll that equals to v.
  Returns nil if none element matches."
  [coll v]
  (let [rf #(if (= v %2) (reduced (reduced %)) (inc %))
        ret (reduce rf 0 coll)]
    (when (reduced? ret) @ret)))

(defn positions
  "Returns a laxy seq of indexes that satisfy pred."
  [f coll]
  (keep-indexed #(when (f %2) %) coll))

(def vconj
  "Like c.c/conj, but creates vector when passed nil as a first arg."
  (fnil conj []))

(defn single?
  "Returns true if coll contains exactly one element,
  otherwise returns false."
  [coll]
  (== 1 (count coll)))

(defn several?
  "Returns true if coll contains more than one item."
  [coll]
  (< 1 (count coll)))

(defn find-first
  "Returns first value x in a supplied coll for which (f x) returns
  a truthy value."
  [f coll]
  (reduce #(when (f %2) (reduced %2)) nil coll))

;;; Set

(defn union*
  "Like c.set/union, but up to 2 args only and uses transients."
  ([] #{})
  ([s1] s1)
  ([s1 s2]
   (if (< (count s1) (count s2))
     (treduce conj! s2 s1)
     (treduce conj! s1 s2))))

(defn set*
  "Like c.c/set but slower and accepts multiple colls."
  [& colls]
  (set (apply concat colls)))

(defn maybe-set
  "Returns flat set of old and val if old is not nil and values are
  not equal. Otherwise returns non nil value.
  A helper for gradual creation of multiple values in a map."
  [old val]
  (cond (nil? old) val
        (nil? val) old
        (and (set? old) (set? val)) (union* old val)
        (set? old) (conj old val)
        (= old val) old
        :else #{old val}))

;;; Vector

(defn maybe-vec
  "Returns vector of old and val if old is not nil and values are
  not equal. Otherwise returns non nil value.
  A helper for gradual creation of multiple values in a map."
  [old val]
  (cond (nil? old) val
        (nil? val) old
        (and (vector? old) (vector? val)) (into old val)
        (vector? old) (conj old val)
        (= old val) old
        :else [old val]))

(defn provide-vector
  "Wraps x in vector if it is not already a persistent vector,
  otherwise returns x. Returns empty vector if nil is given."
  [x]
  (cond (vector? x) x
        (nil? x) []
        :else [x]))

(defn somev
  "Returns vector of items from coll, with nil items removed."
  [coll]
  (vec (remove nil? coll)))

;;; Map

(defn hmap
  "Returns new map from given map entries."
  [entries]
  (into {} entries))

(defn sentinel
  "Returns new sentinel object."
  []
  #?(:clj (Object.)
     :cljs (js/Object.)))

(defn nn-hash-map
  "Like c.c/hash-map but does not include false or nil values."
  [& keyvals]
  (let [sentinel (sentinel)
        pkr (volatile! sentinel)
        rf (fn [m x]
             (let [pk @pkr]
               (if (identical? sentinel pk)
                 (do (vreset! pkr x) m)
                 (do (vreset! pkr sentinel)
                     (if x (assoc! m pk x) m)))))
        ret (treduce rf {} keyvals)]
    (when-not (identical? sentinel @pkr)
      (throw #?(:clj (java.lang.IllegalArgumentException.
                      "nn-hash-map must have even args")
                :cljs (js/Error. "nn-hash-map must have even args"))))
    ret))

(defn some-hash-map
  "Like c.c/hash-map but does not include nil values."
  [& keyvals]
  (let [sentinel (sentinel)
        pkr (volatile! sentinel)
        rf (fn [m x]
             (let [pk @pkr]
               (if (identical? sentinel pk)
                 (do (vreset! pkr x) m)
                 (do (vreset! pkr sentinel)
                     (if (nil? x) m (assoc! m pk x))))))
        ret (treduce rf {} keyvals)]
    (when-not (identical? sentinel @pkr)
      (throw #?(:clj (java.lang.IllegalArgumentException.
                      "nn-hash-map must have even args")
                :cljs (js/Error. "nn-hash-map must have even args"))))
    ret))

(defn assoc-when
  "Like c.c/assoc but does not perform assoc if v is nil or false."
  ([m k v]
   (if v (assoc m k v) m))
  ([m k v & kvs]
   (apply assoc-when (assoc-when m k v) kvs)))

(defn update-when
  "Like c.c/update but does not perform update
  if new value is nil or false."
  ([m k f]
   (assoc-when m k (f (get m k))))
  ([m k f & args]
   (assoc-when m k (apply f (get m k) args))))

(defn assoc-some
  "Like c.c/assoc but does not perform assoc if v is nil."
  ([m k v]
   (if (nil? v) m (assoc m k v)))
  ([m k v & kvs]
   (apply assoc-some (assoc-some m k v) kvs)))

(defn update-some
  "Like c.c/update but does not perform update if new value is nil."
  ([m k f]
   (assoc-some m k (f (get m k))))
  ([m k f & args]
   (assoc-some m k (apply f (get m k) args))))

(defn nn-select-keys
  "Like c.c/select-keys but does not copy nil values."
  [m ks]
  (let [rf #(if-some [v (get m %2)] (assoc! % %2 v) %)
        ret (treduce rf {} ks)]
    (with-meta ret (meta m))))

(defn transform-keys
  "Given a map m and a function a-fn1, will apply that function to all
  keys in the map, recursively (unless non-recursive? is true). Returns
  modified map."
  ([a-fn1 m]
   (transform-keys a-fn1 m true))
  ([a-fn1 m non-recursive?]
   (let [rf (fn [tm [k v]] (assoc! tm (a-fn1 k) v))]
     ;; only apply to maps
     (if non-recursive?
       (treduce rf {} m)
       (postwalk (fn [x] (if (map? x) (treduce rf {} x) x)) m)))))

(defn tzipmap
  "Like c.c/zipmap, but uses transients underneath.
  Flattens keys if flat? is true
  Adapted from http://dev.clojure.org/jira/browse/CLJ-1005"
  ([keys vals]
   (tzipmap keys vals nil))
  ([keys vals flat?]
   (loop [map (transient {})
          ks (seq keys)
          vs (seq vals)]
     (if (and ks vs)
       (recur (if flat?
                (let [v (first vs)
                      rf #(assoc! % %2 v)]
                  (reduce rf map (provide-coll (first ks))))
                (assoc! map (first ks) (first vs)))
              (next ks)
              (next vs))
       (persistent! map)))))

(defn index-by
  "Returns a map which indexes values by the result of calling kfn
  with given value."
  ([kfn values]
   (index-by kfn values nil))
  ([kfn values flat?]
   (tzipmap (map kfn values) values flat?)))

(defn into-index
  "Returns an index map with items from coll added,
  using k to compute item keys."
  ([k coll]
   (into-index nil k coll))
  ([index k coll]
   (treduce #(assoc! % (k %2) %2) (or index {}) coll)))

(defn flat-group-by
  "Like c.c/group-by but flattens keys before inserting."
  [k coll]
  (let [irf (fn [tm k v]
              (let [ov (get tm k)
                    nv (vconj ov v)]
                (assoc! tm k nv)))
        rf (fn [tm v]
             (let [ks (provide-coll (k v))]
               (reduce #(irf % %2 v) tm ks)))]
    (treduce rf {} coll)))

(defn map-by
  "Like c.c/group-by but accepts additional function that is used
  to transform values. Useful if processing maps.
  One arg version uses first and second as kf and vf respectively."
  ([m]
   (map-by m first second))
  ([m kf]
   (let [rf (fn [ret x]
              (let [k (kf x)]
                (assoc! ret k (conj (get ret k []) x))))]
     (treduce rf {} m)))
  ([m kf vf]
   (let [rf (fn [ret x]
              (let [k (kf x)]
                (assoc! ret k (conj (get ret k []) (vf x)))))]
     (treduce rf {} m))))

(defn deep-merge
  "Recursively merges maps. If values are not maps,
  the last value wins. Maps can be empty or nil"
  [& vals]
  (letfn [(m [& xs]
             (if (some #(map? %) xs)
               (apply merge-with m xs)
               (last xs)))]
   (reduce m vals)))

(defn unkeywordize-keys
  "Recursively transforms all map keys from keywords to strings
  retaining namespaces."
  ;; taken from clojure.core, copyright Rich Hickey and contributors
  [m]
  (let [f (fn [[k v]] (if (keyword? k)
                        [(util/unkeyword k) v]
                        [k v]))]
    (walk/postwalk (fn [x] (if (map? x) (into {} (map f x)) x)) m)))

;;;; j.u.HashMap

#?(:clj
   (defn juhmap
     []
     (java.util.HashMap.)))

(defn juhput!
  [m k v]
  (.put ^java.util.HashMap m k v)
  m)

;;; Seqs

(defn mapmap
  "Returns a lazy sequence of items from coll where each item
  is transformed with an additional (map mf item) call."
  [mf coll]
  (map #(map mf %) coll))

(defn mapmapmap
  "Returns a lazy sequence of items from coll where each item
  is transformed with an additional (mapmap mf item) call."
  [mf coll]
  (mapmap #(map mf %) coll))

(defn lazy-mapcat
  "Like c.c/mapcat but fully lazy."
  [f coll]
  (lazy-mapcat* f coll nil))

(defn concat*
  "Like (c.c/mapcat identity coll) but fully lazy."
  [coll]
  (lazy-mapcat* identity coll nil))

(defn partition-into*
  "Applies partitionf to each value in coll, splitting it each time
  partitionf returns a new value. Reduces partitions with reducef,
  calling it with no arguments to obtain inital value for each new
  partition. Finally it calls reducef with reduced partition
  to obtain final collected partition. In you have just 2-arity
  reducef function, you  can add 1-arity to it with completing
  function. Returns a lazy seq of such collected partitions.
  Returns a stateful transducer when coll is not provided."
  ([partitionf reducef]
   ;; modified implementation of c.c./partition-by
   ;; original copyright (C) by Rich Hickey and Clojure contributors.
   (fn [rf]
     (let [rv (volatile! ::none)
           pv (volatile! ::none)]
       (fn
         ([] (rf))
         ([result]
          (let [rval @rv
                result (if (identical? ::none rval)
                         result
                         (let [v (reducef rval)]
                           (vreset! rv ::none)
                           (unreduced (rf result v))))]
            (rf result)))
         ([result input]
          (let [pval @pv
                rval @rv
                val (partitionf input)]
            (vreset! pv val)
            (if (or (identical? pval ::none)
                    (= val pval))
              (let [rval (if (identical? ::none rval)
                           (reducef)
                           rval)]
                (vreset! rv (reducef rval input))
                result)
              (let [v (reducef rval)]
                (vreset! rv ::none)
                (let [ret (rf result v)]
                  (when-not (reduced? ret)
                    (vreset! rv (reducef (reducef) input)))
                  ret)))))))))
  ([partitionf reducef coll]
   ;; modified implementation of c.c./partition-by
   ;; original copyright (C) by Rich Hickey and Clojure contributors.
   (lazy-seq
    (when-let [s (seq coll)]
      (let [fst (first s)
            fv (partitionf fst)
            rst (take-while #(= fv (partitionf %)) (next s))
            run (reduce reducef (reducef) (cons fst rst))]
        (cons (reducef run)
              (partition-into* partitionf reducef
                               (seq (drop (count run) s)))))))))

(defn partition-into
  "Applies partitionf to each value in coll, splitting it each time
  partitionf returns a new value. Reduces partitions with reducef
  begining with init value. Calls reducef with no arguments to obtain
  inital value if one is not given, for each new partition.
  Finally it calls reducef with reduced partition to obtain final
  collected partition. In you have just 2-arity reducef function, you
  can add 1-arity to it with completing function.
  Returns a lazy seq of such collected partitions.
  Returns a stateful transducer when coll is not provided."
  ([partitionf reducef]
   (partition-into* partitionf reducef))
  ([partitionf reducef init]
   (partition-into* partitionf
                    (fn
                      ([] init)
                      ([ret] (reducef ret))
                      ([ret step] (reducef ret step)))))
  ([partitionf reducef init coll]
   (partition-into* partitionf
                    (fn
                      ([] init)
                      ([ret] (reducef ret))
                      ([ret step] (reducef ret step)))
                    coll)))

(defn partition-by-duplicate-or-limit
  "Applies f to each vall in coll, splitting it each time it sees a
  value it has seen before in the current partition or when the
  partition reaches the limit size. Useful for creating batches that
  contain updates for the same id."
  [limit f coll]
  (partition-by
   (let [seen (volatile! #{})
         counter (volatile! 0)]
     (fn [e]
       (let [v (f e)]
         (if (or (contains? @seen v)
                 (= limit @counter))
           (do
             (vreset! seen #{})
             (vreset! counter 0)
             true)
           (do
             (vswap! seen conj v)
             (vswap! counter inc)
             false)))))
   coll))

(defn besides*
  "Calls f for each item in coll, without any arguments.
  Disregards returned value from f and does not modify items
  in any way. Meant to be used for executing side effects like metrics
  or logging. Returns a transducer if coll is not supplied."
  ([f]
   (map #(do (f) %)))
  ([f coll]
   (map #(do (f) %) coll)))

(defn besides
  "Calls f for each item in coll, passing the item as an argument
  to f. Disregards returned value from f and does not modify items
  in any way. Meant to be used for executing side effects like metrics
  or logging. Returns a transducer if coll is not supplied."
  ([f]
   (map #(do (f %) %)))
  ([f coll]
   (map #(do (f %) %) coll)))

#?(:clj
   (defn pmapcat
     "Like c.c/pmap but concatenates returned items."
     ([mf coll]
      (mapcat identity (pmap mf coll)))
     ([mf coll coll2]
      (mapcat identity (pmap mf coll coll2)))
     ([mf coll coll2 & colls]
      (mapcat identity (apply pmap mf coll coll2 colls)))))

(defn progress-coll
  "Returns lazy seq with coll contents, that logs info message for
  each n processed items."
  ([coll n]
   (progress-coll coll n "items processed..."))
  ([coll n msg]
   (let [counter (volatile! 0)
         mf #(let [cnt (vswap! counter inc)]
               (when (zero? (mod cnt n))
                 (info cnt msg))
               %)]
     (map mf coll))))

(defn interleave*
  "Returns a lazy seq of the first item in each coll, then the second
  etc. Exhausts all colls."
  ([] ())
  ([colls]
   (lazy-seq
    (let [ss (seq (keep seq colls))]
      (when ss
        (concat (map first ss) (interleave* (map rest ss))))))))

(defn take-until
  "Returns a lazy sequence of successive items from coll until
  (pred item) returns true, including that item. pred must be
  free of side-effects.
  See: http://dev.clojure.org/jira/browse/CLJ-1451"
  [pred coll]
  (lazy-seq
   (when-let [s (seq coll)]
     (let [x (first s)]
       (cons x (when-not (pred x)
                 (take-until pred (rest s))))))))

(defn sorted-subset?
  "Returns true if coll1 is a subset of coll2, where both colls
  are sorted sequences of items. Uses = for equality."
  [coll1 coll2]
  (loop [xs (seq coll1)
         ys (seq coll2)]
    (let [x (first xs)
          y (first ys)]
      (cond (nil? xs) true
            (nil? ys) false
            (= x y) (recur (next xs) ys)
            :else (recur xs (next ys))))))
#?(:clj
   (defn hfset
     [coll]
     (let [s (java.util.HashSet.)]
       (run! #(.add s %) coll)
       (fn [x]
         (.contains ^java.util.Set s x)))))

(defn subview
  "TODO: docstring"
  [coll offset limit]
  (let [part (if (and offset (pos? offset))
               (drop offset coll)
               coll)]
    (if (and limit (pos? limit))
      (take limit part)
      part)))

;; taken from clojure core
(defn distinct-by
  "Returns a lazy sequence of the elements of coll with duplicates
  removed.
  Returns a stateful transducer when no collection is provided."
  ([k]
   (fn [rf]
     (let [seen (volatile! #{})]
       (fn
         ([] (rf))
         ([result] (rf result))
         ([result input]
          (let [kinput (k input)]
            (if (contains? @seen kinput)
              result
              (do (vswap! seen conj kinput)
                  (rf result input)))))))))
  ([k coll]
   (let [step
         (fn step [xs seen]
           (lazy-seq
            ((fn [[f :as xs] seen]
               (when-let [s (seq xs)]
                 (let [kf (k f)]
                   (if (contains? seen kf)
                     (recur (rest s) seen)
                     (cons f (step (rest s) (conj seen kf)))))))
             xs seen)))]
     (step coll #{}))))


#?(:clj
   (defn fast-distinct
     ([]
      (fn [rf]
        (let [nilf #(if (nil? %) ::nil %)
              seen (java.util.concurrent.ConcurrentHashMap/newKeySet)]
          (fn
            ([] (rf))
            ([result] (rf result))
            ([result input]
             (if (.contains seen (nilf input))
               result
               (do (.add seen (nilf input))
                   (rf result input))))))))
     ([coll]
      (let [nilf #(if (nil? %) ::nil %)
            step
            (fn step [xs seen]
              (lazy-seq
               ((fn [[f :as xs] seen]
                  (when-let [s (seq xs)]
                    (if (.contains ^java.util.Set seen (nilf f))
                      (recur (rest s) seen)
                      (cons f (step (rest s)
                                    (do (.add ^java.util.Set seen (nilf f)) seen))))))
                xs seen)))]
        (step coll (java.util.concurrent.ConcurrentHashMap/newKeySet))))))

#?(:clj
   (defn fast-distinct-by
     ([k]
      (fn [rf]
        (let [nilf #(if (nil? %) ::nil %)
              seen (java.util.concurrent.ConcurrentHashMap/newKeySet)]
          (fn
            ([] (rf))
            ([result] (rf result))
            ([result input]
             (let [kinput (k input)]
               (if (.contains seen (nilf kinput))
                 result
                 (do (.add seen (nilf kinput))
                     (rf result input)))))))))
     ([k coll]
      (let [nilf #(if (nil? %) ::nil %)
            step
            (fn step [xs seen]
              (lazy-seq
               ((fn [[f :as xs] seen]
                  (when-let [s (seq xs)]
                    (let [kf (k f)]
                      (if (.contains ^java.util.Set seen (nilf kf))
                        (recur (rest s) seen)
                        (cons f (step (rest s)
                                      (do (.add ^java.util.Set seen (nilf kf)) seen)))))))
                xs seen)))]
        (step coll (java.util.concurrent.ConcurrentHashMap/newKeySet))))))


(defn snake_casify
  ([m]
   (snake_casify m false))
  ([m non-recursive?]
   (let [kf (fn [k]
              (if (namespace k)
                (keyword (->snake_case (namespace k))
                         (->snake_case (name k)))
                (->snake_case_keyword k)))]
     (transform-keys kf m non-recursive?))))

(defn kebab-casify
  ([m]
   (kebab-casify m true))
  ([m non-recursive?]
   (let [kf (fn [k]
              (if (namespace k)
                (keyword (->kebab-case (namespace k))
                         (->kebab-case (name k)))
                (->kebab-case-keyword k)))]
     (transform-keys kf m non-recursive?))))

(macros/deftime
  (defmacro ->map
    "(->map a b) ;;=> {:a a :b b}"
    [& symbols]
    (assert (every? symbol? symbols))
    `(hash-map
      ~@(interleave (map keyword symbols) symbols))))

#?(:cljs
   (defn shallow-merge
     "(Shallow) merges object o2 into o1. Mutates o1 and returns
     it. Accepts nil for either object by substituting with an empty
     object."
     [o1 o2]
     (doto (or o1 #js {}) (gobj/extend (or o2 #js {})))))

(defn filter-map
  "Filters m according to spec. See tests for examples."
  [x spec]
  (cond
    (not spec) nil
    (sequential? x) (mapv #(filter-map % spec) x)
    (map? spec)
    (let [default (or (get spec :filter/default)
                      (get spec "filter/default")
                      ;; deprecated, TODO: remove soon
                      (get spec :response/default)
                      (get spec "response/default"))
          kf (fn [[k v]]
               (when-not (contains? spec k)
                 [k (filter-map v default)]))
          init (if default (into {} (keep kf x)) {})
          rf (fn [m k v]
               (if (and v (contains? x k))
                 (assoc m k (filter-map (get x k) v))
                 m))]
      (reduce-kv rf init spec))
    :else x))

(defn ffilter
  "Returns first item of a coll that satisfies pred."
  [pred coll]
  (some #(when (pred %) %) coll))

;;;; From Plumatic Plumbing

(macros/deftime
  (defmacro lazy-get
    "Like get but lazy about default"
    [m k d]
    `(if-let [pair# (find ~m ~k)]
       (val pair#)
       ~d)))

(defn kget
  ([m k]
   (kget m k nil))
  ([m k default]
   (or (get m k)
       (get m (util/unkeyword k))
       default)))

(defn safe-get
  "Like get but throw an exception if not found"
  [m k]
  (lazy-get
   m k
   (let [msg (str "Key " k
                  " not found in "
                  (binding [*print-length* 200]
                    (print-str (mapv key m))))]
     #?(:clj (throw (Exception. msg))
        :cljs (throw (js/Error. msg))))))

;;;; End Plumatic Plumbing

(defn left-join
  "Like set.join, but analog to left join."
  ([xrel yrel]
   (if (and (seq xrel) (seq yrel))
     (let [ks (set/intersection (set (keys (first xrel)))
                                (set (keys (first yrel))))
           idx (set/index yrel ks)]
       (reduce (fn [ret x]
                 (if-let [found (idx (select-keys x ks))]
                   (reduce #(conj %1 (merge %2 x)) ret found)
                   (conj ret x)))
               #{} xrel))
     xrel))
  ([xrel yrel km]
   (let [idx (set/index yrel (vals km))]
     (reduce (fn [ret x]
               (if-let [found (idx (set/rename-keys (select-keys x (keys km)) km))]
                 (reduce #(conj %1 (merge %2 x)) ret found)
                 (conj ret x)))
             #{} xrel))))

(def keepv
  (comp vec keep))

(defn head
  [coll]
  (reduce (fn [_ val] (reduced val)) nil coll))
